import type { BoardMeta } from './board.js';
import type { CreateObjectInput } from './objects.js';

export interface TemplateObject extends CreateObjectInput {
  id: string;
}

export interface TemplateContent {
  meta?: Partial<BoardMeta>;
  objects: TemplateObject[];
}

export interface BuiltinTemplate {
  id: string;
  name: string;
  description: string;
  category: 'Ретро' | 'Планирование' | 'Идеи' | 'Схемы' | 'Исследования' | 'Базовые';
  build: () => TemplateContent;
}

let seq = 0;
const id = (p: string) => `${p}${(++seq).toString(36)}${Math.random().toString(36).slice(2, 7)}`;

function sticky(x: number, y: number, text: string, color = 'yellow', parentId: string | null = null): TemplateObject {
  return { id: id('s'), type: 'sticky', x, y, w: 200, h: 200, parentId, props: { color }, text };
}
function frame(x: number, y: number, w: number, h: number, title: string, background = 'fill:white', order = 0): TemplateObject {
  return { id: id('f'), type: 'frame', x, y, w, h, props: { title, background, presentationOrder: order } };
}
function text(x: number, y: number, t: string, fontSize = 32, w = 600): TemplateObject {
  return { id: id('t'), type: 'text', x, y, w, h: fontSize * 1.5, props: { fontSize, autoWidth: false }, text: t };
}
function shape(x: number, y: number, w: number, h: number, kind: string, t = '', fill = 'fill:white', parentId: string | null = null): TemplateObject {
  return { id: id('h'), type: 'shape', x, y, w, h, parentId, props: { kind, fill, stroke: 'ink:ink', strokeWidth: 2 }, text: t };
}
function connector(from: string, to: string, label = ''): TemplateObject {
  return {
    id: id('c'),
    type: 'connector',
    props: { from: { kind: 'object', objectId: from, anchor: 'auto' }, to: { kind: 'object', objectId: to, anchor: 'auto' }, lineType: 'elbow', label },
  };
}
function card(x: number, y: number, title: string, status: string, parentId: string | null, extra: Record<string, unknown> = {}): TemplateObject {
  return { id: id('k'), type: 'card', x, y, w: 260, h: 132, parentId, props: { title, status, ...extra } };
}

/** Колонки ретро: фрейм на каждую + пример стикеров. */
function retro(columns: Array<{ title: string; bg: string; color: string; example: string }>, heading: string): TemplateContent {
  const objects: TemplateObject[] = [text(0, -110, heading, 40, 1200)];
  columns.forEach((c, i) => {
    const f = frame(i * 520, 0, 480, 760, c.title, c.bg, i + 1);
    objects.push(f, sticky(i * 520 + 30, 40, c.example, c.color, f.id), sticky(i * 520 + 250, 40, '', c.color, f.id));
  });
  return { meta: { grid: 'dots' }, objects };
}

export const BUILTIN_TEMPLATES: BuiltinTemplate[] = [
  {
    id: 'builtin:retro-ssc',
    name: 'Ретро: Start / Stop / Continue',
    description: 'Что начать делать, что прекратить и что продолжить.',
    category: 'Ретро',
    build: () =>
      retro(
        [
          { title: 'Начать', bg: 'fill:mint', color: 'mint', example: 'Делать демо в конце спринта' },
          { title: 'Прекратить', bg: 'fill:coral', color: 'coral', example: 'Созвоны без повестки' },
          { title: 'Продолжить', bg: 'fill:sky', color: 'sky', example: 'Парное ревью' },
        ],
        'Ретроспектива спринта',
      ),
  },
  {
    id: 'builtin:retro-msg',
    name: 'Ретро: Mad / Sad / Glad',
    description: 'Эмоции команды: что злило, что расстроило, что порадовало.',
    category: 'Ретро',
    build: () =>
      retro(
        [
          { title: 'Злит 😠', bg: 'fill:coral', color: 'coral', example: 'Нестабильный стенд' },
          { title: 'Расстраивает 😔', bg: 'fill:lavender', color: 'lavender', example: 'Сдвинули релиз' },
          { title: 'Радует 😊', bg: 'fill:lime', color: 'lime', example: 'Новый онбординг' },
        ],
        'Mad · Sad · Glad',
      ),
  },
  {
    id: 'builtin:kanban',
    name: 'Канбан-доска',
    description: 'Колонки-фреймы и карточки задач — перетаскивайте между колонками.',
    category: 'Планирование',
    build: () => {
      const cols = ['К выполнению', 'В работе', 'На проверке', 'Готово'];
      const statuses = ['todo', 'in_progress', 'review', 'done'];
      const objects: TemplateObject[] = [text(0, -110, 'Канбан команды', 40, 1200)];
      const tasks = [['Собрать требования', 'Набросать макет'], ['Сверстать главную'], ['Ревью API'], ['Настроить CI']];
      cols.forEach((c, i) => {
        const f = frame(i * 340, 0, 300, 860, c, 'fill:sand', i + 1);
        objects.push(f);
        tasks[i]!.forEach((t, j) => objects.push(card(i * 340 + 20, 30 + j * 152, t, statuses[i]!, f.id, j === 0 ? { tags: ['дизайн'] } : {})));
      });
      return { meta: { grid: 'dots' }, objects };
    },
  },
  {
    id: 'builtin:story-map',
    name: 'User story map',
    description: 'Активности пользователя, шаги и релизы.',
    category: 'Планирование',
    build: () => {
      const objects: TemplateObject[] = [text(0, -110, 'Карта пользовательских историй', 40, 1200)];
      const acts = ['Найти товар', 'Оформить заказ', 'Получить заказ'];
      acts.forEach((a, i) => {
        objects.push(sticky(i * 480, 0, a, 'peach'));
        objects.push(sticky(i * 480, 230, `Шаг ${i * 2 + 1}`, 'sky'), sticky(i * 480 + 230, 230, `Шаг ${i * 2 + 2}`, 'sky'));
      });
      const r1 = frame(-40, 480, 1460, 280, 'Релиз 1 — MVP', 'fill:lavender', 1);
      const r2 = frame(-40, 800, 1460, 280, 'Релиз 2', 'fill:gray', 2);
      objects.push(r1, r2, sticky(0, 520, 'Поиск по названию', 'yellow', r1.id), sticky(480, 520, 'Оплата картой', 'yellow', r1.id), sticky(0, 840, 'Фильтры', 'yellow', r2.id));
      return { objects };
    },
  },
  {
    id: 'builtin:mindmap',
    name: 'Mind map',
    description: 'Центральная идея и ветки. Tab — новая ветка, Enter — соседняя.',
    category: 'Идеи',
    build: () => {
      const root = id('m');
      const mk = (parent: string, t: string, color: string, dir: 'left' | 'right', order: string, x: number, y: number): TemplateObject => ({
        id: id('m'),
        type: 'mindmap-node',
        x,
        y,
        w: 160,
        h: 44,
        props: { parentNodeId: parent, rootId: root, branchColor: color, direction: dir, order },
        text: t,
      });
      const a = mk(root, 'Цели', 'ink:blue', 'right', 'a0', 156, -80);
      const b = mk(root, 'Аудитория', 'ink:green', 'right', 'a1', 156, 36);
      const c = mk(root, 'Риски', 'ink:red', 'left', 'a2', -316, -80);
      const d = mk(root, 'Ресурсы', 'ink:amber', 'left', 'a3', -316, 36);
      return {
        objects: [
          { id: root, type: 'mindmap-node', x: -100, y: -28, w: 200, h: 56, props: { parentNodeId: null, rootId: root, branchColor: 'ink:violet' }, text: 'Новый продукт' },
          a,
          b,
          c,
          d,
          mk(a.id, 'Рост на 20%', 'ink:blue', 'right', 'a0', 372, -80),
          mk(b.id, 'Малый бизнес', 'ink:green', 'right', 'a0', 372, 36),
          mk(c.id, 'Сроки', 'ink:red', 'left', 'a0', -532, -80),
        ],
      };
    },
  },
  {
    id: 'builtin:flowchart',
    name: 'Блок-схема',
    description: 'Начало, шаги, решение и конец, соединённые линиями.',
    category: 'Схемы',
    build: () => {
      const start = shape(0, 0, 200, 90, 'ellipse', 'Начало', 'fill:mint');
      const step = shape(0, 180, 200, 100, 'rounded', 'Заполнить форму', 'fill:sky');
      const decide = shape(-10, 370, 220, 150, 'diamond', 'Данные верны?', 'fill:yellow');
      const fix = shape(340, 395, 200, 100, 'rounded', 'Исправить', 'fill:peach');
      const end = shape(0, 610, 200, 90, 'ellipse', 'Готово', 'fill:lavender');
      return {
        meta: { grid: 'lines' },
        objects: [start, step, decide, fix, end, connector(start.id, step.id), connector(step.id, decide.id), connector(decide.id, end.id, 'да'), connector(decide.id, fix.id, 'нет'), connector(fix.id, step.id)],
      };
    },
  },
  {
    id: 'builtin:brainstorm',
    name: 'Бриф и брейншторм',
    description: 'Проблема, цель, ограничения и поле для идей.',
    category: 'Идеи',
    build: () => {
      const brief = frame(0, 0, 520, 760, 'Бриф', 'fill:white', 1);
      const ideas = frame(580, 0, 1000, 760, 'Идеи', 'fill:lime', 2);
      return {
        objects: [
          brief,
          ideas,
          { ...text(30, 40, 'Проблема', 24, 460), parentId: brief.id },
          sticky(30, 100, 'Опишите проблему одной фразой', 'gray', brief.id),
          { ...text(30, 330, 'Цель', 24, 460), parentId: brief.id },
          sticky(30, 390, 'Как поймём, что решили?', 'gray', brief.id),
          sticky(620, 40, 'Идея 1', 'yellow', ideas.id),
          sticky(840, 40, 'Идея 2', 'pink', ideas.id),
          sticky(1060, 40, 'Идея 3', 'sky', ideas.id),
        ],
      };
    },
  },
  {
    id: 'builtin:weekly',
    name: 'План на неделю',
    description: 'Семь колонок по дням с задачами-стикерами.',
    category: 'Планирование',
    build: () => {
      const days = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];
      const objects: TemplateObject[] = [text(0, -110, 'Неделя', 40, 1200)];
      days.forEach((d, i) => {
        const f = frame(i * 260, 0, 240, 640, d, i >= 5 ? 'fill:sand' : 'fill:white', i + 1);
        objects.push(f, { ...sticky(i * 260 + 20, 30, '', i >= 5 ? 'peach' : 'yellow', f.id), w: 160, h: 160, props: { color: i >= 5 ? 'peach' : 'yellow', size: 'S' } });
      });
      return { objects };
    },
  },
  {
    id: 'builtin:swot',
    name: 'SWOT-анализ',
    description: 'Сильные и слабые стороны, возможности и угрозы.',
    category: 'Исследования',
    build: () => {
      const q = [
        ['Сильные стороны', 'fill:mint', 'mint'],
        ['Слабые стороны', 'fill:coral', 'coral'],
        ['Возможности', 'fill:sky', 'sky'],
        ['Угрозы', 'fill:peach', 'peach'],
      ] as const;
      const objects: TemplateObject[] = [text(0, -110, 'SWOT', 40, 1200)];
      q.forEach(([t, bg, c], i) => {
        const f = frame((i % 2) * 640, Math.floor(i / 2) * 520, 600, 480, t, bg, i + 1);
        objects.push(f, sticky((i % 2) * 640 + 30, Math.floor(i / 2) * 520 + 40, '', c, f.id));
      });
      return { objects };
    },
  },
  {
    id: 'builtin:journey',
    name: 'Customer journey map',
    description: 'Этапы пути пользователя: действия, мысли, эмоции, возможности.',
    category: 'Исследования',
    build: () => {
      const stages = ['Узнал', 'Выбрал', 'Купил', 'Пользуется'];
      const lanes: Array<[string, string]> = [
        ['Действия', 'sky'],
        ['Мысли', 'lavender'],
        ['Эмоции', 'pink'],
        ['Возможности', 'lime'],
      ];
      const objects: TemplateObject[] = [text(0, -140, 'Путь клиента', 40, 1200)];
      stages.forEach((s, i) => objects.push(shape(220 + i * 260, -60, 240, 50, 'rounded', s, 'fill:ink')));
      lanes.forEach(([l, c], r) => {
        objects.push(text(0, r * 240 + 60, l, 22, 200));
        stages.forEach((_, i) => objects.push({ ...sticky(240 + i * 260, r * 240, '', c), w: 200, h: 200 }));
      });
      return { objects };
    },
  },
  {
    id: 'builtin:blank-grid',
    name: 'Пустая доска с сеткой',
    description: 'Чистый лист с линейной сеткой и привязкой.',
    category: 'Базовые',
    build: () => ({ meta: { grid: 'lines', snapToGrid: true }, objects: [] }),
  },
];

export function findBuiltinTemplate(templateId: string): BuiltinTemplate | undefined {
  return BUILTIN_TEMPLATES.find((t) => t.id === templateId);
}
